<template>
  <div class="holder-analysis-container">
    <h3>股票股东数</h3>
    <div class="input-section">
      <label></label>
      <div class="input-row">
        <input 
          v-model="inputStockCode" 
          placeholder="请输入股票代码, 例如：000001" 
          class="stock-input"
          @keyup.enter="doInputStockCode"
          @focus="selectAllOnFocus"
        />
        <button @click="doInputStockCode" class="analyze-btn">分析</button>
        <div class="year-limit-selector">
          <label class="radio-label">
            <input 
              type="radio" 
              v-model="internalYearLimit" 
              :value="5" 
              class="radio-input"
            />
            <span class="radio-text">5年</span>
          </label>
          <label class="radio-label">
            <input 
              type="radio" 
              v-model="internalYearLimit" 
              :value="3" 
              class="radio-input"
            />
            <span class="radio-text">3年</span>
          </label>
        </div>
      </div>
    </div>
    
    <!-- 加载状态指示器 -->
    <div v-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>
    
    <!-- 无数据提示 -->
    <div v-else-if="noData" class="no-data-section">
      <p>暂无数据</p>
    </div>
    
    <div v-else-if="stockData" class="result-section">
      <div class="result-container">
        <!-- 左侧文字信息 -->
        <div class="info-panel">
          <p><span class="info-label">股票:</span> <strong><router-link :to="{ name: 'HealthCheck', params: { stockCode: stockData.stockCode } }" class="stock-link">{{ stockData.stockName }}({{ stockData.market + stockData.stockCode }})</router-link></strong></p>
          <p><span class="info-label">所属行业:</span> <strong><router-link :to="{ name: 'IndustryPlateHolders', params: { plateId: stockData.secondPlateId, yearLimit: internalYearLimit } }" class="stock-link">{{ stockData.secondPlateName }}</router-link></strong></p>
          <p><span class="info-label">评分:</span> <strong :class="getRankingScoreClass(holderData.score)">{{ holderData.score.toFixed(2) }}</strong> ({{ holderData.holder_score_pos }} / {{ holderData.total_stocks }})</p>
          <p><span class="info-label">最近股东数:</span> <strong>{{ holderData.newestNum }} ({{ holderData.newestDate }})</strong></p>
          <p><span class="info-label">最少股东:</span> <strong>{{ holderData.lowestNum }} ({{ holderData.lowestDate }})</strong></p>
          <p><span class="info-label">最多股东:</span> <strong>{{ holderData.highestNum }} ({{ holderData.highestDate }})</strong></p>
          <p><span class="info-label">上市时间:</span> <strong>{{ stockData.listingDate }}</strong><span class="date-warning">{{ getDateWarning(stockData.listingDate) }}</span></p>
        </div>
        
        <!-- 右侧图表 -->
        <div class="chart-panel">
          <div ref="chartContainer" class="chart-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mockApi from '../api/mock';
import * as echarts from 'echarts';
import { getRankingScoreClass } from '@/utils/utils';

export default {
  name: 'SingleStockHolder',
  props: {
    stockCode: {
      type: String,
      default: ''
    },
    yearLimit: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    // 设置internalYearLimit为默认值5，不再从localStorage读取
    return {
      realStockCode: '',
      inputStockCode: '',
      internalYearLimit: 5, // 默认选中5年
      stockData: null,
      loading: false,  // 添加加载状态
      noData: false,   // 添加无数据状态
      chart: null
    };
  },
  computed: {
    holderData() {
      // 根据internalYearLimit的值从stockData.score_3y或者stockData.score_5y获取数据
      if (!this.stockData) {
        return null;
      }
      
      // 根据internalYearLimit的值选择对应的数据
      if (this.internalYearLimit === 3 && this.stockData.score_3y) {
        return this.stockData.score_3y;
      } else if (this.internalYearLimit === 5 && this.stockData.score_5y) {
        return this.stockData.score_5y;
      }
      
      // 默认返回null或其他适当的默认值
      return null;
    }
  },
  watch: {
      stockData: {
        handler(newVal) {
          if (newVal) {
            console.log("stockData new value: ", newVal);
            this.$nextTick(() => {
              this.initChart();
            });
          }
        },
        deep: true
      },
      realStockCode: {
        handler(newVal, oldVal) {
          // 当internalStockCode变化时，不调用analyze方法
          // 但是需要通知页面刷新数据，因为holderData依赖internalStockCode
          // 由于holderData是计算属性，它会自动更新
          // 我们只需要重新初始化图表来反映新的数据
          console.log("realStockCode new value: ", newVal, "old: ", oldVal);
          if (newVal != oldVal) {
            this.fetchHolderData(newVal);
          }
        }
      },
      internalYearLimit: {
        handler(newVal) {
          // 当internalYearLimit变化时，不调用analyze方法
          // 但是需要通知页面刷新数据，因为holderData依赖internalYearLimit
          // 由于holderData是计算属性，它会自动更新
          // 我们只需要重新初始化图表来反映新的数据
          console.log("internalYearLimit new value: ", newVal);
          this.$nextTick(() => {
            if (newVal != this.yearLimit) {
              const newParams = {
                stockCode: this.stockCode,
                yearLimit: newVal.toString()
              };
              this.$router.replace({ 
                name: 'SingleStockHolder', 
                params: newParams
              });
            }
            this.initChart();
          });
        }
      },
      // 监听路由参数变化
      '$route'(to, from) {
        if (to.name !== 'SingleStockHolder') {
          return;
        }
        console.log("SingleStockHolder: route change from ", from, " to ", to, " stockCode: ", this.realStockCode);
        this.internalYearLimit = to.params.yearLimit ? parseInt(to.params.yearLimit) : 5;
        this.realStockCode = to.params.stockCode || '';
        if (to.params.stockCode != this.inputStockCode) {
          this.inputStockCode = '';
        }
      }
    },
  async mounted() {
    // 如果有传入的stockCode参数，则自动分析
    console.log("SingleStockHolder mounted, stockCode: ", this.stockCode, " realStockCode: ", this.realStockCode);
    this.realStockCode = this.stockCode;
    if (this.stockCode) {
      // 如果路由参数中有yearLimit，则设置internalYearLimit为该值
      if (this.yearLimit) {
        this.internalYearLimit = parseInt(this.yearLimit);
      }
    }
    // 不再监听storage事件
  },
  methods: {
    getRankingScoreClass,
    async fetchHolderData(stockCode) {
      // 重置状态
      this.stockData = null;
      this.noData = false;
      
      if (stockCode == '') {
        this.loading = false;
        return;
      }
      
      // 开始加载
      this.loading = true;
      
      try {
        const response = await mockApi.getHolders(stockCode);
        // 完成加载
        this.loading = false;
        
        if (response.success && response.data) {
          this.stockData = response.data;
        } else {
          // 没有数据
          this.noData = true;
        }
      } catch (error) {
        console.error('查询失败，请检查股票代码是否正确:', error);
        // 完成加载，但显示无数据
        this.loading = false;
        this.noData = true;
      }
    },
    async doInputStockCode() {
      console.log("doInputStockCode, inputStockCode: ", this.inputStockCode);
      if (!this.inputStockCode) {
        alert('请输入股票代码');
        return;
      }
      // 重置状态
      this.stockData = null;
      this.noData = false;
      this.loading = false;
      if (this.$router) {
        // 构造新的路由参数
        const newParams = {
          stockCode: this.inputStockCode,
          yearLimit: this.internalYearLimit.toString()
        };
        this.$router.replace({ 
          name: 'SingleStockHolder', 
          params: newParams
        }).catch(err => {
          // 忽略导航重复的错误
          if (err.name !== 'NavigationDuplicated') {
            console.error('路由导航错误:', err);
          }
        });
      }
    },
    selectAllOnFocus(event) {
      // 在下一个tick中选中所有文本，确保DOM已更新
      this.$nextTick(() => {
        event.target.select();
      });
    },
    initChart() {
      console.log("initChart, container: ", this.$refs.chartContainer);
      if (!this.$refs.chartContainer) {
        return;
      }
      // 销毁之前的图表实例
      if (this.chart) {
        this.chart.dispose();
      }
      
      // 初始化图表
      this.chart = echarts.init(this.$refs.chartContainer);
      
      // 准备图表数据
      let holderData = [];
      if (this.holderData && this.holderData.holder_nums) {
        holderData = this.holderData.holder_nums.map(item => [
          item.holderDate,
          item.holderNum
        ]);
      } else if (this.stockData && this.stockData.holder_nums) {
        // 如果holderData不可用，回退到stockData
        holderData = this.stockData.holder_nums.map(item => [
          item.holderDate,
          item.holderNum
        ]);
      }
      
      // 图表配置
      const option = {
        title: {
          text: '股东数',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            const date = params[0].name;
            const count = params[0].value;
            return `${date}<br/>股东数: ${count}`;
          }
        },
        xAxis: {
          type: 'category',
          data: holderData.map(item => item[0]),
          axisLabel: {
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: '股东数'
        },
        series: [{
          data: holderData.map(item => item[1]),
          type: 'line',
          smooth: false
        }]
      };
      
      // 设置图表配置并渲染
      this.chart.setOption(option);
    },
    getDateWarning(listingDate) {
      // 如果listingDate距离今天<3年，则返回"(距今不满3年)"，否则返回""
      if (!listingDate) {
        return "";
      }
      
      // 将listingDate转换为Date对象
      const listing = new Date(listingDate);
      const today = new Date();
      
      // 计算时间差（毫秒）
      const timeDiff = today.getTime() - listing.getTime();
      
      // 将毫秒转换为年数
      const yearsDiff = timeDiff / (1000 * 60 * 60 * 24 * 365);
      
      // 如果小于3年，返回警告信息
      return yearsDiff < 3 ? "(距今不满3年)" : "";
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }
};
</script>

<style scoped>
.holder-analysis-container {
  padding: 20px;
  max-width: 100vw;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-section {
  margin-bottom: 20px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.input-section label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: #2c3e50;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}.stock-input {
  flex: 1;
  min-width: 200px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
}

.analyze-btn {
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.analyze-btn:hover {
  background-color: #359268;
}

.year-limit-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1em;
  color: #2c3e50;
}

.radio-input {
  margin-right: 5px;
  transform: scale(1.2);
}

.radio-text {
  white-space: nowrap;
}

.result-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.result-section h4 {
  margin-top: 0;
  color: #2c3e50;
}

/* 加载状态样式 */
.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 无数据提示样式 */
.no-data-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
  font-size: 1.1em;
}

/* 新增的布局样式 */
.result-container {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.info-panel {
  flex: 2;
  min-width: 200px;
}

.chart-panel {
  flex: 3;
  min-width: 300px;
}

.chart-container {
  width: 100%;
  height: 400px;
  max-width: 100%;
  overflow-x: hidden;
}

.info-panel p {
  margin: 10px 0;
  line-height: 1.5;
}

.info-label {
  display: inline-block;
  width: 100px;
}

.date-warning {
  color: #e74c3c;
  font-weight: bold;
  margin-left: 10px;
}

@media (max-width: 768px) {
  .input-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .stock-input {
    min-width: unset;
  }
  
  .year-limit-selector {
    justify-content: center;
    padding: 10px 0;
  }
  
  /* 移动端垂直布局 */
  .result-container {
    flex-direction: column;
  }
  
  .info-panel,
  .chart-panel {
    flex: none;
  }
}
</style>